/**
 * The Coding-owned SCC mint/ensure command — MR-WORKSPACE-SCC-INVERSION
 * (program task 1.5, ARCFE-986).
 *
 * Two things landed here in one atomic move:
 *
 * 1. `mintCodingCaseAccessContext` TRANSFERRED from
 *    `packages/workspace/src/data/api/entitlements.ts`, where it was the
 *    design doc's own named example of a disguised cross-domain command — a
 *    raw `POST /coding-cases/access-context` self-annotated `@domain coding`
 *    inside workspace's api, invisible to `checkBackboneInvariants` (which
 *    reads import specifiers, not routes). It now lives with its domain.
 *
 * 2. `ensureSccAccessContext` — the orchestration that previously ran INLINE
 *    in workspace's entitlements queryFn through five injected callbacks.
 *    Moved VERBATIM: the prepare → mint → logout-race-check → set/error/deny
 *    sequence, the swallow-and-mark error handling, and the exact legacy log
 *    line. Workspace now knows nothing about SCC; it awaits one
 *    domain-neutral port and the app wires that port to this command.
 *
 * This is a separate module from `scc-access-context.ts` ON PURPOSE: the
 * store's header requires it stay transport-free so auth/logout can clear
 * the cache without pulling the api graph. The command imports transport;
 * the store must not.
 */
import { getScribeHttpClients } from '@heidi/api/client';
import { logger } from '@heidi/runtime/logger';
import { type MintCodingCaseAccessContextResponse } from '../../contracts';
import {
  getSccAccessStatus,
  markSccAccessDenied,
  markSccAccessError,
  prepareSccAccessMint,
  setSccAccessContext,
} from './scc-access-context';

/** @domain coding — mint SCC coding JWT (RCM-851) */
export const mintCodingCaseAccessContext = () => {
  return getScribeHttpClients().scribeHttpV2.post<MintCodingCaseAccessContextResponse>(
    '/coding-cases/access-context',
    undefined,
    // Keep entitlements bootstrap from hanging if mint stalls.
    { timeout: 15_000 }
  );
};

/**
 * Ensure the SCC access context matches the caller's coder-seat state.
 *
 * CONTRACT: never rejects. The legacy inline block swallowed mint failures
 * (warn + markSccAccessError) so the entitlements query always resolved;
 * callers await this for ORDERING (the JWT is settled before the query
 * resolves), not for errors. Every call with `coderSeatEnabled` re-mints —
 * the legacy queryFn minted on every fetch (token refresh re-keys the query
 * and must re-mint), so "ensure" means ensure-fresh, not ensure-once.
 */
export async function ensureSccAccessContext({
  coderSeatEnabled,
}: {
  coderSeatEnabled: boolean;
}): Promise<void> {
  if (!coderSeatEnabled) {
    markSccAccessDenied();
    return;
  }

  // Allow remint after a prior logout denial for this new fetch.
  prepareSccAccessMint();
  try {
    const { data: minted } = await mintCodingCaseAccessContext();
    // Logout may have denied access while mint was in flight.
    if (getSccAccessStatus() === 'denied') {
      return;
    }
    setSccAccessContext({
      access_token: minted.access_token,
      expires_at: minted.expires_at,
    });
  } catch (error) {
    if (getSccAccessStatus() === 'denied') {
      return;
    }
    logger.warn('[SCC] Failed to mint coding access context', error);
    markSccAccessError();
  }
}
